'use client';

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Modal } from '@/components/modals/Modal';
import { Button, Typography } from '@/components/ui-components';
import { theme } from '@/config/theme';
import { i18n, replacePlaceholders } from '@/config/i18n';

/**
 * OTP Modal Props Interface
 */
export interface OTPModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (otp: string) => void;
  username: string;
  isLoading?: boolean;
  error?: string;
}

/**
 * Styled Components
 */
const ModalContent = styled.div`
  padding: 2rem;
  width: 100%;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const TitleContainer = styled.div`
  margin-bottom: 1rem;
`;

const DescriptionContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const UsernameText = styled.span`
  font-weight: 600;
  color: #FB6F2A;
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const StyledInput = styled.input<{ $hasError?: boolean }>`
  text-align: center;
  font-size: 1.5rem;
  letter-spacing: 0.5rem;
  font-weight: 600;
  padding: 1rem;
  height: 3.5rem;
  border-radius: 0.75rem;
  border: 2px solid ${props => props.$hasError ? theme.colors.error : theme.colors.gray[300]};
  transition: all 0.2s ease-in-out;
  
  &:focus {
    outline: none;
    border-color: #FB6F2A;
    box-shadow: 0 0 0 3px rgba(251, 111, 42, 0.1);
  }
  
  &:disabled {
    background-color: ${theme.colors.gray[50]};
    cursor: not-allowed;
    opacity: 0.6;
  }

  &::placeholder {
    font-size: 0.875rem;
    letter-spacing: normal;
    font-weight: 400;
    color: ${theme.colors.gray[400]};
  }
`;

const ErrorContainer = styled.div`
  margin-top: 0.5rem;
  text-align: center;
`;

const ErrorText = styled.span`
  color: ${theme.colors.error};
  font-size: 0.875rem;
`;

const TimerContainer = styled.div`
  text-align: center;
  margin: 1rem 0;
`;

const TimerText = styled.span`
  color: ${theme.colors.gray[500]};
  font-size: 0.875rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const StyledButton = styled(Button)`
  flex: 1;
  height: 3rem;
  font-weight: 600;
  border-radius: 0.75rem;
  transition: all 0.2s ease-in-out;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ExpiredContainer = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: ${theme.colors.gray[50]};
  border-radius: 0.75rem;
  border: 1px solid ${theme.colors.gray[200]};
`;

const ExpiredText = styled.span`
  color: ${theme.colors.gray[600]};
  font-size: 0.875rem;
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
`;

const OTPIcon = styled.div`
  width: 4rem;
  height: 4rem;
  background: linear-gradient(135deg, #D33324, #FB6F2A);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  font-weight: bold;
`;

/**
 * OTP Modal Component
 * Displays a modal for OTP input during login process
 */
export const OTPModal: React.FC<OTPModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  username,
  isLoading = false,
  error,
}) => {
  const [otp, setOtp] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);

  // Timer for OTP expiration (10 minutes)
  useEffect(() => {
    if (isOpen) {
      setTimeLeft(600); // 10 minutes in seconds
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isOpen]);

  // Reset OTP when modal opens
  useEffect(() => {
    if (isOpen) {
      setOtp('');
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (otp?.length === 6 && timeLeft > 0) {
      onSubmit(otp);
    }
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const numericValue = pastedData.replace(/\D/g, '').slice(0, 6);
    setOtp(numericValue);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="">
      <ModalContent>
        <HeaderSection>
          <IconContainer>
            <OTPIcon>🔐</OTPIcon>
          </IconContainer>
          
          <TitleContainer>
            <Typography variant="heading-large">
              {i18n.login.otp.title}
            </Typography>
          </TitleContainer>
          
          <DescriptionContainer>
            <Typography variant="body-regular">
              {i18n.login.otp.description}
              <UsernameText>{username}</UsernameText>
            </Typography>
          </DescriptionContainer>
        </HeaderSection>

        <FormContainer onSubmit={handleSubmit}>
          <InputContainer>
            <StyledInput
              type="text"
              inputMode="numeric"
              value={otp}
              onChange={handleOtpChange}
              onPaste={handlePaste}
              placeholder={i18n.login.otp.placeholder}
              disabled={isLoading || timeLeft === 0}
              $hasError={!!error}
              autoFocus
            />
            {error && (
              <ErrorContainer>
                <ErrorText>{error}</ErrorText>
              </ErrorContainer>
            )}
          </InputContainer>

          {timeLeft > 0 && (
            <TimerContainer>
              <TimerText>
                {replacePlaceholders(i18n.login.otp.codeExpires, { time: formatTime(timeLeft) })}
              </TimerText>
            </TimerContainer>
          )}

          <ButtonContainer>
            <StyledButton
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              {i18n.common.buttons.cancel}
            </StyledButton>
            <StyledButton
              type="submit"
              variant="primary"
              disabled={otp?.length !== 6 || isLoading || timeLeft === 0}
            >
              {isLoading ? i18n.login.otp.verifying : i18n.login.otp.verifyButton}
            </StyledButton>
          </ButtonContainer>
        </FormContainer>

        {timeLeft === 0 && (
          <ExpiredContainer>
            <ExpiredText>
              {i18n.login.otp.expired}
            </ExpiredText>
          </ExpiredContainer>
        )}
      </ModalContent>
    </Modal>
  );
};


